import React, { useEffect, useRef, useState } from 'react';

// Calm fade-and-rise wrapper for marketing-page sections. Uses
// IntersectionObserver so each block animates in once when it enters the
// viewport. Honors prefers-reduced-motion (no animation, content shows
// immediately).

interface FadeInProps {
  children: React.ReactNode;
  delay?: number; // ms
  duration?: number; // ms
  distance?: number; // px of upward drift
  className?: string;
  as?: 'div' | 'section' | 'article' | 'span' | 'header';
  threshold?: number;
}

const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
};

export const FadeIn: React.FC<FadeInProps> = ({
  children,
  delay = 0,
  duration = 500,
  distance = 20,
  className = '',
  as: Tag = 'div',
  threshold = 0.1,
}) => {
  const [visible, setVisible] = useState<boolean>(prefersReducedMotion());
  const ref = useRef<HTMLElement | null>(null);
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (visible) return; // either reduced-motion or already triggered
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !triggeredRef.current) {
            triggeredRef.current = true;
            window.setTimeout(() => setVisible(true), delay);
            observer.disconnect();
          }
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [delay, threshold, visible]);

  return React.createElement(
    Tag,
    {
      ref: ref as any,
      className: `motion-safe:will-change-transform ${className}`,
      style: {
        transitionProperty: 'opacity, transform',
        transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
        transitionDuration: `${duration}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : `translateY(${distance}px)`,
      },
    },
    children,
  );
};

export default FadeIn;
